import bridge from './bridge';
import { matchesOfflineEndpoint } from './endpoints';
import { apiRequestThroughBridge, OfflineUnsupportedError } from './apiRequest';

let _online = true;
bridge.call('network.status').then((s) => { _online = !!s.online; }).catch(() => {});
bridge.on('network.changed', (p) => { _online = !!p.online; });

const isFileUpload = (url) => /\/common\/file_upload\//.test(url || '');

// Read a Blob/File as raw base64 (no data: prefix) so it can cross the JSON bridge.
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const r = reader.result || '';
      const comma = r.indexOf(',');
      resolve(comma >= 0 ? r.slice(comma + 1) : r);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Offline file capture: pull the File(s) out of the multipart FormData, base64-encode them, and hand
// them to the native bridge which stores them locally and returns placeholder urls.
async function handleOfflineFileUpload(config) {
  const fd = config.data;
  const files = [];
  if (fd && typeof fd.entries === 'function') {
    for (const [key, value] of fd.entries()) {
      if (value && typeof value === 'object' && typeof value.arrayBuffer === 'function') {
        files.push({
          key,
          base64: await blobToBase64(value),
          filename: value.name || 'file',
          mime: value.type || 'application/octet-stream',
        });
      }
    }
  }
  const directory = (config.url.match(/\/common\/file_upload\/([^/?]+)/) || [])[1] || 'dynamicForm';
  return apiRequestThroughBridge({
    method: 'POST',
    endpoint: config.url,
    body: { __fileUpload: true, directory, files },
  });
}

/**
 * The cached `_online` flag can lag reality: on a cold start the device briefly reports "not
 * connected" before the real state arrives, and a request fired in that window was sent to the
 * offline cache even though the network was fine (an online ticket then opened empty, because the
 * cache had never seen it). When the flag says offline, confirm with the device before trusting it.
 */
async function isOnlineNow() {
  if (_online) return true;
  try {
    const s = await bridge.call('network.status');
    _online = !!s?.online;
  } catch (e) {
    /* keep the cached flag */
  }
  return _online;
}

const isCacheMiss = (out) =>
  out?.status === 404 && out?.data?.error === 'not_in_offline_cache';

export function installOfflineInterceptor(axiosInstance) {
  axiosInstance.interceptors.request.use(async (config) => {
    if (await isOnlineNow()) return config; // online → passthrough
    if (!matchesOfflineEndpoint(config.method, config.url)) {
      throw new OfflineUnsupportedError(config.url);
    }
    // Short-circuit: call the bridge directly, then convert to a fake axios response.
    const out = isFileUpload(config.url)
      ? await handleOfflineFileUpload(config)
      : await apiRequestThroughBridge({
          method: (config.method || 'GET').toUpperCase(),
          endpoint: config.url,
          body: config.data,
          if_match: config.headers?.['If-Match'] || config.headers?.['if-match'],
        });
    // Nothing cached for this read and the network is (now) available: use the network instead of
    // handing the page an empty answer. Real offline cache misses still surface as before.
    if (isCacheMiss(out) && (await isOnlineNow())) return config;
    // Mark the request so the response interceptor skips production decryption — offline bridge
    // responses are already plain JSON (decrypting them would throw and drop the data).
    config.__isOffline = true;
    config.adapter = () => Promise.resolve({
      data: out.data, status: out.status || 200,
      statusText: 'OK', headers: {}, config, request: null,
    });
    return config;
  });
}
